import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useReducedMotion } from "framer-motion";
import { BACKEND_URL } from "../lib/api";
import NewsletterForm from "../components/NewsletterForm";
import { usePageTitle } from "../lib/usePageTitle";
import PremiumBackdrop from "../components/PremiumBackdrop";

const API = `${BACKEND_URL}/api`;

/* ------------------------------------------------------------------ */
/*  Field Notes archive  ·  minimalist grid with heroic type + hooks.  */
/* ------------------------------------------------------------------ */

/* One-line Human Tone hooks per slug. Fallback to note.subtitle. */
const HOOKS = {
  "virtual-growth-lab":
    "A quiet room where three ideas met and refused to leave until they turned into something we could build.",
  "the-companion":
    "A conversation with my mother that ended one career and started another.",
  "small-leaks":
    "The story of a business that never lost a fight and quietly lost the war.",
  "one-per-city":
    "Why we say no to more work than we say yes to, and why the founders we serve prefer it that way.",
  "the-quiet-room":
    "A room without slides, without pitches, without noise. The kind of room founders come back to.",
};

/* Word-by-word wave reveal on scroll — matches Journey / HowWeWork. */
function WaveReveal({ text, className = "" }) {
  const reduced = useReducedMotion();
  if (reduced) return <span className={className}>{text}</span>;
  const words = (text || "").split(" ");
  return (
    <span className={className}>
      {words.map((w, i) => (
        <span
          key={i}
          className="inline-block overflow-hidden align-baseline pr-[0.28em]"
        >
          <motion.span
            className="inline-block"
            variants={{
              hidden: { y: "110%" },
              visible: {
                y: 0,
                transition: {
                  duration: 0.85,
                  ease: [0.16, 1, 0.3, 1],
                  delay: i * 0.055,
                },
              },
            }}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

/* Mount-based word slide (for hero above-the-fold). */
function MountWordSlide({ text, delay = 0 }) {
  const reduced = useReducedMotion();
  if (reduced) return <span>{text}</span>;
  const words = (text || "").split(" ");
  return (
    <span>
      {words.map((w, i) => (
        <span
          key={i}
          className="inline-block overflow-hidden align-baseline pr-[0.28em]"
        >
          <motion.span
            className="inline-block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{
              duration: 0.9,
              ease: [0.16, 1, 0.3, 1],
              delay: delay + i * 0.06,
            }}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

export default function FieldNotes() {
  usePageTitle("Field Notes");
  const [notes, setNotes] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/field-notes`)
      .then((r) => setNotes(r.data))
      .catch((e) => setError(e?.message || "Could not load field notes"));
  }, []);

  return (
    <main
      className="relative bg-bg text-ink pt-32 md:pt-40 pb-28 overflow-hidden gvg-typography"
      data-testid="field-notes-index"
    >
      <PremiumBackdrop />
      {/* Atmospheric backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 45% at 50% 25%, rgba(201,169,97,0.08), transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.035]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='220' height='220' filter='url(%23n)' opacity='0.55'/></svg>\")",
        }}
      />

      {/* HERO */}
      <section className="relative mx-auto max-w-7xl px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="font-sans text-xs uppercase tracking-mega text-brass mb-8 flex items-center gap-3"
        >
          <span className="inline-block h-px w-8 bg-brass/60" />
          Field Notes · Sunday letters
        </motion.p>

        <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[104px] leading-[0.98] text-ink max-w-5xl">
          <MountWordSlide text="Short, honest stories" />
          <br />
          <span className="text-ink/60">
            <MountWordSlide text="from inside real businesses." delay={0.35} />
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 md:mt-14 font-sans text-ink-muted max-w-xl text-base md:text-lg leading-relaxed"
        >
          Not advice. Not theory. Real people, real problems, and the small
          things that changed everything. A two-minute read, or a
          two-minute listen in Krishna&rsquo;s voice.
        </motion.p>
      </section>

      {/* ARCHIVE */}
      <section
        className="relative mx-auto max-w-7xl px-6 md:px-12 mt-28 md:mt-36"
        data-testid="field-notes-archive"
      >
        {error && (
          <p className="font-sans text-sm text-ink-muted">{error}</p>
        )}
        {!notes && !error && (
          <p className="font-sans text-sm text-ink-muted">Loading&hellip;</p>
        )}
        {notes && notes.length === 0 && (
          <p className="font-serif italic text-xl text-ink-muted">
            The first field note is being written. Please come back soon.
          </p>
        )}

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {(notes || []).map((n, i) => {
            const hook = HOOKS[n.slug] || n.subtitle || "";
            const noteNum = String(i + 1).padStart(2, "0");
            return (
              <motion.article
                key={n.id || n.slug}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={{
                  hidden: {},
                  visible: {
                    transition: {
                      staggerChildren: 0.08,
                      delayChildren: 0.05,
                    },
                  },
                }}
                className="group relative"
                data-testid={`field-note-card-${n.slug}`}
              >
                <Link
                  to={`/field-notes/${n.slug}`}
                  className="relative block rounded-2xl border border-brass/15 hover:border-brass/50 bg-black/25 backdrop-blur-[1px] p-8 md:p-10 lg:p-12 overflow-hidden transition-colors duration-500"
                  style={{
                    transitionTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)",
                  }}
                  data-testid={`field-note-read-${n.slug}`}
                >
                  {/* Top-edge champagne highlight */}
                  <span
                    aria-hidden
                    className="absolute inset-x-10 top-0 h-px opacity-40 group-hover:opacity-80 transition-opacity duration-500"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent, rgba(201,169,97,0.7), transparent)",
                    }}
                  />
                  {/* Soft hover bloom */}
                  <span
                    aria-hidden
                    className="absolute -inset-8 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                    style={{
                      background:
                        "radial-gradient(ellipse at center, rgba(201,169,97,0.08), transparent 70%)",
                      filter: "blur(20px)",
                    }}
                  />

                  {/* Kicker row */}
                  <motion.div
                    variants={{
                      hidden: { opacity: 0, y: 10 },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.7,
                          ease: [0.16, 1, 0.3, 1],
                        },
                      },
                    }}
                    className="relative flex items-center gap-3 mb-8"
                  >
                    <span className="h-px w-6 bg-brass" />
                    <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
                      Note {noteNum}
                      <span className="text-ink-muted mx-2">·</span>
                      <span className="text-ink-muted">
                        {formatDate(n.published_at)}
                      </span>
                    </p>
                  </motion.div>

                  {/* Heroic title with WordSlide wave reveal */}
                  <h2 className="relative font-serif text-3xl md:text-4xl lg:text-[44px] leading-[1.05] text-ink group-hover:text-brass transition-colors duration-500">
                    <WaveReveal text={n.title} />
                  </h2>

                  {/* One-line Human Tone hook */}
                  {hook && (
                    <motion.p
                      variants={{
                        hidden: { opacity: 0, y: 14 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.9,
                            delay: 0.3,
                            ease: [0.16, 1, 0.3, 1],
                          },
                        },
                      }}
                      className="relative mt-6 font-serif italic text-lg md:text-xl text-ink-muted leading-relaxed"
                    >
                      {hook}
                    </motion.p>
                  )}

                  {/* Meta */}
                  <motion.div
                    variants={{
                      hidden: { opacity: 0 },
                      visible: {
                        opacity: 1,
                        transition: { duration: 0.9, delay: 0.55 },
                      },
                    }}
                    className="relative mt-10 flex items-center justify-between gap-4"
                  >
                    <span className="font-sans text-[10px] uppercase tracking-mega text-ink-muted">
                      Read {n.read_minutes || 2} min
                      <span className="text-line mx-2">·</span>
                      Listen in Krishna&rsquo;s voice
                    </span>
                    <span className="font-sans text-[10px] uppercase tracking-mega text-brass opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      Read the note &rarr;
                    </span>
                  </motion.div>
                </Link>
              </motion.article>
            );
          })}
        </div>
      </section>

      <section className="relative mx-auto max-w-4xl px-6 md:px-12 mt-28 md:mt-36">
        <NewsletterForm source="field-notes-index" />
      </section>
    </main>
  );
}